import { graphQLClient } from '@api/client';
import { MedicalTypeEnum, useUntactDepartmentsQuery } from '@api/GoodocAPI';
import { useTreatmentInformationContext } from '@context/TreatmentInformation';
import { UntactDepartmentObject } from '@context/TreatmentInformation/types';
import { FocusHandler, useFocusable } from '@noriginmedia/norigin-spatial-navigation';
import { UNTACT_DEPARTMENT_LIST_ITEM_FIRST } from '@pages/Main/focuskey';
import { generateDoctorSelectionPath } from '@router/path';
import constate from 'constate';
import { useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useBanner from '@context/Main/hooks/useBanner';
import { BannerType, DepartmentType, DepartmentWithBanner } from '@context/Main/types';

const useMain = () => {
  const navigate = useNavigate();
  const { setSelectedUntactDepartment, setSelectedDoctorId } =
    useTreatmentInformationContext();
  const { data: untactDepartmentQuery } = useUntactDepartmentsQuery(graphQLClient, {
    input: {
      types: [
        MedicalTypeEnum.Medicine,
        MedicalTypeEnum.DentalMedicine,
        MedicalTypeEnum.KoreanMedicine
      ],
      tvVisibility: true
    }
  });
  const { banners, currentBanner, ...bannerFunctions } = useBanner();
  const scrollRef = useRef<HTMLDivElement>(null);

  const { setFocus } = useFocusable();

  const setFirstUntactDepartmentListItem = useCallback(() => {
    setFocus(UNTACT_DEPARTMENT_LIST_ITEM_FIRST);
  }, [setFocus]);

  const onFocus: FocusHandler = useCallback(
    (x) => {
      if (scrollRef.current) {
        scrollRef.current.style.transform = `translateX(${-x.x}px)`;
      }
    },
    [scrollRef]
  );

  const untactDepartmentsWithBanner = useMemo(() => {
    if (!untactDepartmentQuery?.untactDepartments) {
      return;
    }

    let departments: DepartmentWithBanner[] = untactDepartmentQuery.untactDepartments
      .map((value) => {
        return { objectType: 'UntactDepartment', object: value } as DepartmentType;
      })
      .sort((prev, next) => {
        return prev.object.displayOrder - next.object.displayOrder;
      });

    if (banners) {
      const reversedBanner = banners.sort(
        (prev, next) => next.displayOrder - prev.displayOrder
      );

      reversedBanner.forEach((banner) => {
        departments = [
          ...departments.slice(0, banner.displayOrder),
          { objectType: 'UntactTvBanner', object: banner } as BannerType,
          ...departments.slice(banner.displayOrder)
        ];
      });
    }

    return departments;
  }, [untactDepartmentQuery, banners]);

  const moveToDoctorSelect = (untactDepartment: UntactDepartmentObject) => {
    setSelectedUntactDepartment(untactDepartment);
    setSelectedDoctorId('');
    navigate(generateDoctorSelectionPath(untactDepartment.id));
  };

  return {
    untactDepartmentsWithBanner,
    setFirstUntactDepartmentListItem,
    scrollRef,
    banners,
    currentBanner,
    onFocus,
    moveToDoctorSelect,
    showAdvertisement: bannerFunctions.showAdvertisement,
    hideAdvertisement: bannerFunctions.hideAdvertisement,
    navigateAdvertisePage: bannerFunctions.navigateAdvertisePage
  };
};

export const [MainProvider, useMainContext] = constate(useMain);
